// Search (⌘K): the sidebar entry, the dialog and its rows (POLISH PL-16, DESIGN-SPEC §6.18).
//
// Namespace `search`; owner PL-16. One name everywhere: "Search". Placeholders are
// `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // One name for the sidebar entry, the dialog and its field
  name: 'Search',
  description:
    'Search your chats and your company’s files, Skills, results, knowledge and meetings.',
  placeholder: 'Search your workspace',
  close: 'Close search',
  results: 'Search results',
  // Result groups, sentence case
  groupActions: 'Actions',
  groupRecent: 'Recent',
  groupChats: 'Chats',
  groupResults: 'Results',
  groupFiles: 'Files',
  groupSkills: 'Skills',
  groupKnowledge: 'Knowledge',
  groupMeetings: 'Meetings',
  // Each row's kind, read by assistive technology
  kindChat: 'Chat',
  kindFile: 'File',
  kindSkill: 'Skill',
  kindSkillDraft: 'Skill draft',
  kindResult: 'Result',
  kindKnowledgePage: 'Approved knowledge',
  kindMeeting: 'Meeting',
  // Actions
  newChat: 'New chat',
  newChatHint: 'Start a fresh conversation',
  createSkill: 'Create a Skill',
  createSkillHint: 'Build reusable company instructions in chat',
  upload: 'Upload files',
  uploadHint: 'Add source material to your company library',
  uploadLabel: 'Choose files to upload',
  // States
  searching: 'Searching your workspace…',
  loading: 'Loading your workspace…',
  limited: 'Meaning search is temporarily limited. Showing available text matches.',
  // A search that failed: the human line, and the raw cause behind Details
  failed: 'Couldn’t search right now.',
  retry: 'Try search again',
  details: 'Details',
  loadMore: 'Load more',
  noResults: 'No results for “{query}”',
  ask: 'Ask Poneva about “{query}”',
  updated: 'Updated {date}',
  // Footer: ↑↓ Navigate · ↵ Open · Esc Close
  navigate: 'Navigate',
  open: 'Open',
  dismiss: 'Close',
  // Opening a found item failed (it could not be resolved right now)
  openFailed: 'Couldn’t open it right now. Try again.',
};

export type SearchCopyKey = keyof typeof en;

const searchCopy: NamespaceCatalog<SearchCopyKey> = {
  bg: {
    name: 'Търсене',
    description:
      'Търсете в своите чатове и във файловете, Skills, резултатите, знанията и срещите на компанията.',
    placeholder: 'Търсене в работното пространство',
    close: 'Затваряне на търсенето',
    results: 'Резултати от търсенето',
    groupActions: 'Действия',
    groupRecent: 'Скорошни',
    groupChats: 'Чатове',
    groupResults: 'Резултати',
    groupFiles: 'Файлове',
    groupSkills: 'Skills',
    groupKnowledge: 'Знания',
    groupMeetings: 'Срещи',
    kindChat: 'Чат',
    kindFile: 'Файл',
    kindSkill: 'Skill',
    kindSkillDraft: 'Чернова на Skill',
    kindResult: 'Резултат',
    kindKnowledgePage: 'Одобрено знание',
    kindMeeting: 'Среща',
    newChat: 'Нов чат',
    newChatHint: 'Започнете нов разговор',
    createSkill: 'Създаване на Skill',
    createSkillHint: 'Създавайте в чата фирмени инструкции за многократна употреба',
    upload: 'Качване на файлове',
    uploadHint: 'Добавете изходни материали в библиотеката на компанията',
    uploadLabel: 'Изберете файлове за качване',
    searching: 'Търсене в работното пространство…',
    loading: 'Зареждане на работното пространство…',
    limited:
      'Търсенето по смисъл е временно ограничено. Показват се наличните текстови съвпадения.',
    failed: 'Търсенето не е достъпно в момента.',
    retry: 'Търсете отново',
    details: 'Подробности',
    loadMore: 'Зареждане на още',
    noResults: 'Няма резултати за „{query}“',
    ask: 'Попитайте Poneva за „{query}“',
    updated: 'Обновено {date}',
    navigate: 'Навигация',
    open: 'Отваряне',
    dismiss: 'Затваряне',
    openFailed: 'Не може да се отвори в момента. Опитайте отново.',
  },
  hr: {
    name: 'Pretraži',
    description:
      'Pretražujte svoje razgovore te datoteke, Skills, rezultate, znanje i sastanke svoje tvrtke.',
    placeholder: 'Pretraži radni prostor',
    close: 'Zatvori pretraživanje',
    results: 'Rezultati pretraživanja',
    groupActions: 'Radnje',
    groupRecent: 'Nedavno',
    groupChats: 'Razgovori',
    groupResults: 'Rezultati',
    groupFiles: 'Datoteke',
    groupSkills: 'Skills',
    groupKnowledge: 'Znanje',
    groupMeetings: 'Sastanci',
    kindChat: 'Razgovor',
    kindFile: 'Datoteka',
    kindSkill: 'Skill',
    kindSkillDraft: 'Nacrt Skilla',
    kindResult: 'Rezultat',
    kindKnowledgePage: 'Odobreno znanje',
    kindMeeting: 'Sastanak',
    newChat: 'Novi chat',
    newChatHint: 'Započnite novi razgovor',
    createSkill: 'Izradi Skill',
    createSkillHint: 'Izradite u chatu upute tvrtke za višekratnu upotrebu',
    upload: 'Prenesi datoteke',
    uploadHint: 'Dodajte izvorni materijal u knjižnicu tvrtke',
    uploadLabel: 'Odaberite datoteke za prijenos',
    searching: 'Pretraživanje radnog prostora…',
    loading: 'Učitavanje radnog prostora…',
    limited:
      'Pretraživanje po značenju privremeno je ograničeno. Prikazuju se dostupna tekstualna podudaranja.',
    failed: 'Pretraživanje trenutačno nije moguće.',
    retry: 'Pretraži ponovno',
    details: 'Pojedinosti',
    loadMore: 'Učitaj više',
    noResults: 'Nema rezultata za „{query}”',
    ask: 'Pitaj Ponevu o „{query}”',
    updated: 'Ažurirano {date}',
    navigate: 'Kretanje',
    open: 'Otvori',
    dismiss: 'Zatvori',
    openFailed: 'Trenutačno se ne može otvoriti. Pokušajte ponovno.',
  },
  cs: {
    name: 'Hledat',
    description:
      'Prohledávejte své chaty a soubory, Skills, výsledky, znalosti a schůzky své firmy.',
    placeholder: 'Hledat v pracovním prostoru',
    close: 'Zavřít hledání',
    results: 'Výsledky hledání',
    groupActions: 'Akce',
    groupRecent: 'Nedávné',
    groupChats: 'Chaty',
    groupResults: 'Výsledky',
    groupFiles: 'Soubory',
    groupSkills: 'Skills',
    groupKnowledge: 'Znalosti',
    groupMeetings: 'Schůzky',
    kindChat: 'Chat',
    kindFile: 'Soubor',
    kindSkill: 'Skill',
    kindSkillDraft: 'Koncept Skillu',
    kindResult: 'Výsledek',
    kindKnowledgePage: 'Schválené znalosti',
    kindMeeting: 'Schůzka',
    newChat: 'Nový chat',
    newChatHint: 'Zahájit novou konverzaci',
    createSkill: 'Vytvořit Skill',
    createSkillHint: 'Vytvořte v chatu opakovaně použitelné firemní pokyny',
    upload: 'Nahrát soubory',
    uploadHint: 'Přidejte zdrojové materiály do firemní knihovny',
    uploadLabel: 'Vyberte soubory k nahrání',
    searching: 'Prohledávání pracovního prostoru…',
    loading: 'Načítání pracovního prostoru…',
    limited: 'Vyhledávání podle významu je dočasně omezené. Zobrazují se dostupné textové shody.',
    failed: 'Vyhledávání teď není k dispozici.',
    retry: 'Hledat znovu',
    details: 'Podrobnosti',
    loadMore: 'Načíst další',
    noResults: 'Pro „{query}“ nebyly nalezeny žádné výsledky',
    ask: 'Zeptat se Ponevy na „{query}“',
    updated: 'Aktualizováno {date}',
    navigate: 'Procházet',
    open: 'Otevřít',
    dismiss: 'Zavřít',
    openFailed: 'Teď to nejde otevřít. Zkuste to znovu.',
  },
  da: {
    name: 'Søg',
    description: 'Søg i dine chats og i virksomhedens filer, Skills, resultater, viden og møder.',
    placeholder: 'Søg i arbejdsområdet',
    close: 'Luk søgning',
    results: 'Søgeresultater',
    groupActions: 'Handlinger',
    groupRecent: 'Seneste',
    groupChats: 'Chats',
    groupResults: 'Resultater',
    groupFiles: 'Filer',
    groupSkills: 'Skills',
    groupKnowledge: 'Viden',
    groupMeetings: 'Møder',
    kindChat: 'Chat',
    kindFile: 'Fil',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill-kladde',
    kindResult: 'Resultat',
    kindKnowledgePage: 'Godkendt viden',
    kindMeeting: 'Møde',
    newChat: 'Ny chat',
    newChatHint: 'Start en ny samtale',
    createSkill: 'Opret en Skill',
    createSkillHint: 'Byg genanvendelige virksomhedsinstruktioner i chatten',
    upload: 'Upload filer',
    uploadHint: 'Tilføj kildemateriale til virksomhedens bibliotek',
    uploadLabel: 'Vælg filer, der skal uploades',
    searching: 'Søger i arbejdsområdet…',
    loading: 'Indlæser arbejdsområdet…',
    limited: 'Betydningssøgning er midlertidigt begrænset. Viser tilgængelige tekstmatch.',
    failed: 'Søgningen virker ikke lige nu.',
    retry: 'Søg igen',
    details: 'Detaljer',
    loadMore: 'Indlæs flere',
    noResults: 'Ingen resultater for »{query}«',
    ask: 'Spørg Poneva om »{query}«',
    updated: 'Opdateret {date}',
    navigate: 'Navigér',
    open: 'Åbn',
    dismiss: 'Luk',
    openFailed: 'Kunne ikke åbne det lige nu. Prøv igen.',
  },
  nl: {
    name: 'Zoeken',
    description:
      'Zoek in je chats en in de bestanden, Skills, resultaten, kennis en vergaderingen van je bedrijf.',
    placeholder: 'Zoeken in je werkruimte',
    close: 'Zoeken sluiten',
    results: 'Zoekresultaten',
    groupActions: 'Acties',
    groupRecent: 'Onlangs geopend',
    groupChats: 'Chats',
    groupResults: 'Resultaten',
    groupFiles: 'Bestanden',
    groupSkills: 'Skills',
    groupKnowledge: 'Kennis',
    groupMeetings: 'Vergaderingen',
    kindChat: 'Chat',
    kindFile: 'Bestand',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill-concept',
    kindResult: 'Resultaat',
    kindKnowledgePage: 'Goedgekeurde kennis',
    kindMeeting: 'Vergadering',
    newChat: 'Nieuwe chat',
    newChatHint: 'Een nieuw gesprek beginnen',
    createSkill: 'Een Skill maken',
    createSkillHint: 'Herbruikbare bedrijfsinstructies maken in de chat',
    upload: 'Bestanden uploaden',
    uploadHint: 'Bronmateriaal toevoegen aan de bedrijfsbibliotheek',
    uploadLabel: 'Bestanden kiezen om te uploaden',
    searching: 'Werkruimte doorzoeken…',
    loading: 'Werkruimte laden…',
    limited:
      'Zoeken op betekenis is tijdelijk beperkt. Beschikbare tekstovereenkomsten worden getoond.',
    failed: 'Zoeken lukt nu even niet.',
    retry: 'Opnieuw zoeken',
    details: 'Details',
    loadMore: 'Meer laden',
    noResults: 'Geen resultaten voor ‘{query}’',
    ask: 'Vraag Poneva naar ‘{query}’',
    updated: 'Bijgewerkt op {date}',
    navigate: 'Navigeren',
    open: 'Openen',
    dismiss: 'Sluiten',
    openFailed: 'Kan het nu niet openen. Probeer het opnieuw.',
  },
  en,
  et: {
    name: 'Otsing',
    description:
      'Otsi oma vestlustest ning ettevõtte failidest, Skillidest, tulemustest, teadmistest ja koosolekutest.',
    placeholder: 'Otsi tööruumist',
    close: 'Sulge otsing',
    results: 'Otsingutulemused',
    groupActions: 'Toimingud',
    groupRecent: 'Hiljutised',
    groupChats: 'Vestlused',
    groupResults: 'Tulemused',
    groupFiles: 'Failid',
    groupSkills: 'Skillid',
    groupKnowledge: 'Teadmised',
    groupMeetings: 'Koosolekud',
    kindChat: 'Vestlus',
    kindFile: 'Fail',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skilli mustand',
    kindResult: 'Tulemus',
    kindKnowledgePage: 'Kinnitatud teadmine',
    kindMeeting: 'Koosolek',
    newChat: 'Uus vestlus',
    newChatHint: 'Alusta uut vestlust',
    createSkill: 'Loo Skill',
    createSkillHint: 'Loo vestluses korduvkasutatavaid ettevõtte juhiseid',
    upload: 'Laadi failid üles',
    uploadHint: 'Lisa lähtematerjali ettevõtte teeki',
    uploadLabel: 'Vali üleslaaditavad failid',
    searching: 'Otsin tööruumist…',
    loading: 'Laadin tööruumi…',
    limited: 'Tähendusotsing on ajutiselt piiratud. Kuvatakse saadaolevad tekstivasted.',
    failed: 'Otsing ei ole praegu võimalik.',
    retry: 'Otsi uuesti',
    details: 'Üksikasjad',
    loadMore: 'Laadi veel',
    noResults: 'Päringule „{query}” ei leitud tulemusi',
    ask: 'Küsi Ponevalt „{query}” kohta',
    updated: 'Uuendatud {date}',
    navigate: 'Liigu',
    open: 'Ava',
    dismiss: 'Sulge',
    openFailed: 'Seda ei saanud praegu avada. Proovi uuesti.',
  },
  fi: {
    name: 'Haku',
    description:
      'Hae chateistasi sekä yrityksesi tiedostoista, Skilleistä, tuloksista, tietämyksestä ja kokouksista.',
    placeholder: 'Hae työtilasta',
    close: 'Sulje haku',
    results: 'Hakutulokset',
    groupActions: 'Toiminnot',
    groupRecent: 'Viimeisimmät',
    groupChats: 'Chatit',
    groupResults: 'Tulokset',
    groupFiles: 'Tiedostot',
    groupSkills: 'Skillit',
    groupKnowledge: 'Tietämys',
    groupMeetings: 'Kokoukset',
    kindChat: 'Chat',
    kindFile: 'Tiedosto',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skillin luonnos',
    kindResult: 'Tulos',
    kindKnowledgePage: 'Hyväksytty tietämys',
    kindMeeting: 'Kokous',
    newChat: 'Uusi chat',
    newChatHint: 'Aloita uusi keskustelu',
    createSkill: 'Luo Skill',
    createSkillHint: 'Rakenna uudelleenkäytettäviä yritysohjeita chatissa',
    upload: 'Lataa tiedostoja',
    uploadHint: 'Lisää lähdeaineistoa yrityksen kirjastoon',
    uploadLabel: 'Valitse ladattavat tiedostot',
    searching: 'Haetaan työtilasta…',
    loading: 'Ladataan työtilaa…',
    limited: 'Merkityshaku on tilapäisesti rajoitettu. Näytetään saatavilla olevat tekstiosumat.',
    failed: 'Haku ei onnistu juuri nyt.',
    retry: 'Hae uudelleen',
    details: 'Lisätiedot',
    loadMore: 'Lataa lisää',
    noResults: 'Ei tuloksia haulle ”{query}”',
    ask: 'Kysy Ponevalta: ”{query}”',
    updated: 'Päivitetty {date}',
    navigate: 'Siirry',
    open: 'Avaa',
    dismiss: 'Sulje',
    openFailed: 'Sitä ei voitu avata juuri nyt. Yritä uudelleen.',
  },
  fr: {
    name: 'Rechercher',
    description:
      'Recherchez dans vos discussions et dans les fichiers, Skills, résultats, connaissances et réunions de votre entreprise.',
    placeholder: 'Rechercher dans l’espace de travail',
    close: 'Fermer la recherche',
    results: 'Résultats de recherche',
    groupActions: 'Actions',
    groupRecent: 'Récents',
    groupChats: 'Discussions',
    groupResults: 'Résultats',
    groupFiles: 'Fichiers',
    groupSkills: 'Skills',
    groupKnowledge: 'Connaissances',
    groupMeetings: 'Réunions',
    kindChat: 'Discussion',
    kindFile: 'Fichier',
    kindSkill: 'Skill',
    kindSkillDraft: 'Brouillon de Skill',
    kindResult: 'Résultat',
    kindKnowledgePage: 'Connaissance approuvée',
    kindMeeting: 'Réunion',
    newChat: 'Nouvelle discussion',
    newChatHint: 'Commencer une nouvelle conversation',
    createSkill: 'Créer un Skill',
    createSkillHint: 'Créer des instructions d’entreprise réutilisables dans la discussion',
    upload: 'Importer des fichiers',
    uploadHint: 'Ajouter des documents sources à la bibliothèque de l’entreprise',
    uploadLabel: 'Choisir les fichiers à importer',
    searching: 'Recherche dans l’espace de travail…',
    loading: 'Chargement de l’espace de travail…',
    limited:
      'La recherche sémantique est temporairement limitée. Affichage des correspondances textuelles disponibles.',
    failed: 'Impossible de rechercher pour le moment.',
    retry: 'Relancer la recherche',
    details: 'Détails',
    loadMore: 'Afficher plus',
    noResults: 'Aucun résultat pour « {query} »',
    ask: 'Demander à Poneva à propos de « {query} »',
    updated: 'Mis à jour le {date}',
    navigate: 'Naviguer',
    open: 'Ouvrir',
    dismiss: 'Fermer',
    openFailed: 'Impossible de l’ouvrir pour le moment. Réessayez.',
  },
  de: {
    name: 'Suche',
    description:
      'Durchsuchen Sie Ihre Chats sowie die Dateien, Skills, Ergebnisse, das Wissen und die Besprechungen Ihres Unternehmens.',
    placeholder: 'Arbeitsbereich durchsuchen',
    close: 'Suche schließen',
    results: 'Suchergebnisse',
    groupActions: 'Aktionen',
    groupRecent: 'Zuletzt verwendet',
    groupChats: 'Chats',
    groupResults: 'Ergebnisse',
    groupFiles: 'Dateien',
    groupSkills: 'Skills',
    groupKnowledge: 'Wissen',
    groupMeetings: 'Besprechungen',
    kindChat: 'Chat',
    kindFile: 'Datei',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill-Entwurf',
    kindResult: 'Ergebnis',
    kindKnowledgePage: 'Freigegebenes Wissen',
    kindMeeting: 'Besprechung',
    newChat: 'Neuer Chat',
    newChatHint: 'Eine neue Unterhaltung beginnen',
    createSkill: 'Skill erstellen',
    createSkillHint: 'Wiederverwendbare Unternehmensanweisungen im Chat erstellen',
    upload: 'Dateien hochladen',
    uploadHint: 'Quellmaterial zur Unternehmensbibliothek hinzufügen',
    uploadLabel: 'Dateien zum Hochladen auswählen',
    searching: 'Arbeitsbereich wird durchsucht…',
    loading: 'Arbeitsbereich wird geladen…',
    limited:
      'Die Bedeutungssuche ist vorübergehend eingeschränkt. Verfügbare Texttreffer werden angezeigt.',
    failed: 'Die Suche ist gerade nicht möglich.',
    retry: 'Erneut suchen',
    details: 'Details',
    loadMore: 'Mehr laden',
    noResults: 'Keine Ergebnisse für „{query}“',
    ask: 'Poneva zu „{query}“ fragen',
    updated: 'Aktualisiert {date}',
    navigate: 'Navigieren',
    open: 'Öffnen',
    dismiss: 'Schließen',
    openFailed: 'Konnte gerade nicht geöffnet werden. Versuchen Sie es erneut.',
  },
  el: {
    name: 'Αναζήτηση',
    description:
      'Αναζητήστε στις συνομιλίες σας και στα αρχεία, τα Skills, τα αποτελέσματα, τη γνώση και τις συσκέψεις της εταιρείας σας.',
    placeholder: 'Αναζήτηση στον χώρο εργασίας',
    close: 'Κλείσιμο αναζήτησης',
    results: 'Αποτελέσματα αναζήτησης',
    groupActions: 'Ενέργειες',
    groupRecent: 'Πρόσφατα',
    groupChats: 'Συνομιλίες',
    groupResults: 'Αποτελέσματα',
    groupFiles: 'Αρχεία',
    groupSkills: 'Skills',
    groupKnowledge: 'Γνώση',
    groupMeetings: 'Συσκέψεις',
    kindChat: 'Συνομιλία',
    kindFile: 'Αρχείο',
    kindSkill: 'Skill',
    kindSkillDraft: 'Πρόχειρο Skill',
    kindResult: 'Αποτέλεσμα',
    kindKnowledgePage: 'Εγκεκριμένη γνώση',
    kindMeeting: 'Σύσκεψη',
    newChat: 'Νέα συνομιλία',
    newChatHint: 'Ξεκινήστε μια νέα συζήτηση',
    createSkill: 'Δημιουργία Skill',
    createSkillHint: 'Δημιουργήστε στη συνομιλία επαναχρησιμοποιήσιμες οδηγίες της εταιρείας',
    upload: 'Μεταφόρτωση αρχείων',
    uploadHint: 'Προσθέστε υλικό πηγής στη βιβλιοθήκη της εταιρείας',
    uploadLabel: 'Επιλέξτε αρχεία για μεταφόρτωση',
    searching: 'Αναζήτηση στον χώρο εργασίας…',
    loading: 'Φόρτωση του χώρου εργασίας…',
    limited:
      'Η αναζήτηση βάσει νοήματος είναι προσωρινά περιορισμένη. Εμφανίζονται οι διαθέσιμες αντιστοιχίες κειμένου.',
    failed: 'Η αναζήτηση δεν είναι δυνατή αυτή τη στιγμή.',
    retry: 'Νέα αναζήτηση',
    details: 'Λεπτομέρειες',
    loadMore: 'Φόρτωση περισσότερων',
    noResults: 'Δεν βρέθηκαν αποτελέσματα για «{query}»',
    ask: 'Ρωτήστε το Poneva για «{query}»',
    updated: 'Ενημερώθηκε {date}',
    navigate: 'Πλοήγηση',
    open: 'Άνοιγμα',
    dismiss: 'Κλείσιμο',
    openFailed: 'Δεν είναι δυνατό να ανοίξει αυτή τη στιγμή. Δοκιμάστε ξανά.',
  },
  hu: {
    name: 'Keresés',
    description:
      'Keressen a csevegéseiben, valamint a vállalat fájljaiban, Skilljeiben, eredményeiben, tudásában és megbeszéléseiben.',
    placeholder: 'Keresés a munkaterületen',
    close: 'Keresés bezárása',
    results: 'Keresési eredmények',
    groupActions: 'Műveletek',
    groupRecent: 'Legutóbbiak',
    groupChats: 'Csevegések',
    groupResults: 'Eredmények',
    groupFiles: 'Fájlok',
    groupSkills: 'Skillek',
    groupKnowledge: 'Tudás',
    groupMeetings: 'Megbeszélések',
    kindChat: 'Csevegés',
    kindFile: 'Fájl',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill-piszkozat',
    kindResult: 'Eredmény',
    kindKnowledgePage: 'Jóváhagyott tudás',
    kindMeeting: 'Megbeszélés',
    newChat: 'Új csevegés',
    newChatHint: 'Új beszélgetés indítása',
    createSkill: 'Skill létrehozása',
    createSkillHint: 'Újrafelhasználható vállalati utasítások készítése a csevegésben',
    upload: 'Fájlok feltöltése',
    uploadHint: 'Forrásanyag hozzáadása a vállalati könyvtárhoz',
    uploadLabel: 'Válassza ki a feltöltendő fájlokat',
    searching: 'Keresés a munkaterületen…',
    loading: 'A munkaterület betöltése…',
    limited:
      'A jelentés szerinti keresés átmenetileg korlátozott. A rendelkezésre álló szöveges találatok láthatók.',
    failed: 'A keresés most nem érhető el.',
    retry: 'Keresés újra',
    details: 'Részletek',
    loadMore: 'Továbbiak betöltése',
    noResults: 'Nincs találat erre: „{query}”',
    ask: 'Kérdezze a Ponevát erről: „{query}”',
    updated: 'Frissítve: {date}',
    navigate: 'Navigálás',
    open: 'Megnyitás',
    dismiss: 'Bezárás',
    openFailed: 'Most nem nyitható meg. Próbálja újra.',
  },
  ga: {
    name: 'Cuardaigh',
    description:
      'Cuardaigh i do chomhráite agus i gcomhaid, Skills, torthaí, eolas agus cruinnithe do chomhlachta.',
    placeholder: 'Cuardaigh sa spás oibre',
    close: 'Dún an cuardach',
    results: 'Torthaí cuardaigh',
    groupActions: 'Gníomhartha',
    groupRecent: 'Le déanaí',
    groupChats: 'Comhráite',
    groupResults: 'Torthaí',
    groupFiles: 'Comhaid',
    groupSkills: 'Skills',
    groupKnowledge: 'Eolas',
    groupMeetings: 'Cruinnithe',
    kindChat: 'Comhrá',
    kindFile: 'Comhad',
    kindSkill: 'Skill',
    kindSkillDraft: 'Dréacht Skill',
    kindResult: 'Toradh',
    kindKnowledgePage: 'Eolas faofa',
    kindMeeting: 'Cruinniú',
    newChat: 'Comhrá nua',
    newChatHint: 'Tosaigh comhrá úr',
    createSkill: 'Cruthaigh Skill',
    createSkillHint: 'Cruthaigh treoracha athúsáidte don chomhlacht sa chomhrá',
    upload: 'Uaslódáil comhaid',
    uploadHint: 'Cuir ábhar foinse le leabharlann an chomhlachta',
    uploadLabel: 'Roghnaigh comhaid le huaslódáil',
    searching: 'Ag cuardach sa spás oibre…',
    loading: 'Ag lódáil an spáis oibre…',
    limited:
      'Tá cuardach de réir brí teoranta faoi láthair. Taispeántar na meaitseálacha téacs atá ar fáil.',
    failed: 'Ní féidir cuardach a dhéanamh faoi láthair.',
    retry: 'Cuardaigh arís',
    details: 'Sonraí',
    loadMore: 'Lódáil tuilleadh',
    noResults: 'Níl aon torthaí do “{query}”',
    ask: 'Fiafraigh de Poneva faoi “{query}”',
    updated: 'Nuashonraithe {date}',
    navigate: 'Nascleanúint',
    open: 'Oscail',
    dismiss: 'Dún',
    openFailed: 'Ní féidir é a oscailt faoi láthair. Bain triail eile as.',
  },
  it: {
    name: 'Cerca',
    description:
      'Cerca nelle tue chat e in file, Skills, risultati, conoscenze e riunioni della tua azienda.',
    placeholder: 'Cerca nello spazio di lavoro',
    close: 'Chiudi la ricerca',
    results: 'Risultati della ricerca',
    groupActions: 'Azioni',
    groupRecent: 'Recenti',
    groupChats: 'Chat',
    groupResults: 'Risultati',
    groupFiles: 'File',
    groupSkills: 'Skills',
    groupKnowledge: 'Conoscenze',
    groupMeetings: 'Riunioni',
    kindChat: 'Chat',
    kindFile: 'File',
    kindSkill: 'Skill',
    kindSkillDraft: 'Bozza di Skill',
    kindResult: 'Risultato',
    kindKnowledgePage: 'Conoscenza approvata',
    kindMeeting: 'Riunione',
    newChat: 'Nuova chat',
    newChatHint: 'Inizia una nuova conversazione',
    createSkill: 'Crea uno Skill',
    createSkillHint: 'Crea istruzioni aziendali riutilizzabili in chat',
    upload: 'Carica file',
    uploadHint: 'Aggiungi materiale di partenza alla libreria aziendale',
    uploadLabel: 'Scegli i file da caricare',
    searching: 'Ricerca nello spazio di lavoro…',
    loading: 'Caricamento dello spazio di lavoro…',
    limited:
      'La ricerca per significato è temporaneamente limitata. Vengono mostrate le corrispondenze testuali disponibili.',
    failed: 'Al momento non è possibile cercare.',
    retry: 'Riprova la ricerca',
    details: 'Dettagli',
    loadMore: 'Carica altro',
    noResults: 'Nessun risultato per «{query}»',
    ask: 'Chiedi a Poneva di «{query}»',
    updated: 'Aggiornato il {date}',
    navigate: 'Naviga',
    open: 'Apri',
    dismiss: 'Chiudi',
    openFailed: 'Impossibile aprirlo in questo momento. Riprova.',
  },
  lv: {
    name: 'Meklēt',
    description:
      'Meklējiet savās tērzēšanās un uzņēmuma failos, Skills, rezultātos, zināšanās un sanāksmēs.',
    placeholder: 'Meklēt darbvietā',
    close: 'Aizvērt meklēšanu',
    results: 'Meklēšanas rezultāti',
    groupActions: 'Darbības',
    groupRecent: 'Nesenie',
    groupChats: 'Tērzēšanas',
    groupResults: 'Rezultāti',
    groupFiles: 'Faili',
    groupSkills: 'Skills',
    groupKnowledge: 'Zināšanas',
    groupMeetings: 'Sanāksmes',
    kindChat: 'Tērzēšana',
    kindFile: 'Fails',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill melnraksts',
    kindResult: 'Rezultāts',
    kindKnowledgePage: 'Apstiprinātas zināšanas',
    kindMeeting: 'Sanāksme',
    newChat: 'Jauna tērzēšana',
    newChatHint: 'Sākt jaunu sarunu',
    createSkill: 'Izveidot Skill',
    createSkillHint: 'Veidot atkārtoti izmantojamas uzņēmuma instrukcijas tērzēšanā',
    upload: 'Augšupielādēt failus',
    uploadHint: 'Pievienot avota materiālus uzņēmuma bibliotēkai',
    uploadLabel: 'Izvēlēties augšupielādējamos failus',
    searching: 'Meklē darbvietā…',
    loading: 'Ielādē darbvietu…',
    limited:
      'Meklēšana pēc nozīmes īslaicīgi ir ierobežota. Tiek rādītas pieejamās teksta atbilstības.',
    failed: 'Pašlaik nevar veikt meklēšanu.',
    retry: 'Meklēt vēlreiz',
    details: 'Sīkāka informācija',
    loadMore: 'Ielādēt vairāk',
    noResults: 'Nav rezultātu vaicājumam “{query}”',
    ask: 'Jautāt Poneva par “{query}”',
    updated: 'Atjaunināts {date}',
    navigate: 'Pārvietoties',
    open: 'Atvērt',
    dismiss: 'Aizvērt',
    openFailed: 'Pašlaik to nevar atvērt. Mēģiniet vēlreiz.',
  },
  lt: {
    name: 'Paieška',
    description:
      'Ieškokite savo pokalbiuose ir įmonės failuose, Skills, rezultatuose, žiniose ir susitikimuose.',
    placeholder: 'Ieškoti darbo srityje',
    close: 'Uždaryti paiešką',
    results: 'Paieškos rezultatai',
    groupActions: 'Veiksmai',
    groupRecent: 'Naujausi',
    groupChats: 'Pokalbiai',
    groupResults: 'Rezultatai',
    groupFiles: 'Failai',
    groupSkills: 'Skills',
    groupKnowledge: 'Žinios',
    groupMeetings: 'Susitikimai',
    kindChat: 'Pokalbis',
    kindFile: 'Failas',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill juodraštis',
    kindResult: 'Rezultatas',
    kindKnowledgePage: 'Patvirtintos žinios',
    kindMeeting: 'Susitikimas',
    newChat: 'Naujas pokalbis',
    newChatHint: 'Pradėti naują pokalbį',
    createSkill: 'Sukurti Skill',
    createSkillHint: 'Kurkite pakartotinai naudojamas įmonės instrukcijas pokalbyje',
    upload: 'Įkelti failus',
    uploadHint: 'Pridėkite šaltinio medžiagą į įmonės biblioteką',
    uploadLabel: 'Pasirinkite įkeliamus failus',
    searching: 'Ieškoma darbo srityje…',
    loading: 'Įkeliama darbo sritis…',
    limited: 'Paieška pagal prasmę laikinai ribota. Rodomi prieinami teksto atitikmenys.',
    failed: 'Šiuo metu nepavyksta ieškoti.',
    retry: 'Ieškoti dar kartą',
    details: 'Išsamiau',
    loadMore: 'Įkelti daugiau',
    noResults: 'Pagal „{query}“ nieko nerasta',
    ask: 'Klausti Poneva apie „{query}“',
    updated: 'Atnaujinta {date}',
    navigate: 'Naršyti',
    open: 'Atidaryti',
    dismiss: 'Uždaryti',
    openFailed: 'Šiuo metu nepavyksta atidaryti. Bandykite dar kartą.',
  },
  mt: {
    name: 'Fittex',
    description:
      'Fittex fil-chats tiegħek u fil-fajls, Skills, riżultati, għarfien u laqgħat tal-kumpanija tiegħek.',
    placeholder: 'Fittex fl-ispazju tax-xogħol',
    close: 'Agħlaq it-tfittxija',
    results: 'Riżultati tat-tfittxija',
    groupActions: 'Azzjonijiet',
    groupRecent: 'Riċenti',
    groupChats: 'Chats',
    groupResults: 'Riżultati',
    groupFiles: 'Fajls',
    groupSkills: 'Skills',
    groupKnowledge: 'Għarfien',
    groupMeetings: 'Laqgħat',
    kindChat: 'Chat',
    kindFile: 'Fajl',
    kindSkill: 'Skill',
    kindSkillDraft: 'Abbozz ta’ Skill',
    kindResult: 'Riżultat',
    kindKnowledgePage: 'Għarfien approvat',
    kindMeeting: 'Laqgħa',
    newChat: 'Chat ġdid',
    newChatHint: 'Ibda konversazzjoni ġdida',
    createSkill: 'Oħloq Skill',
    createSkillHint: 'Oħloq istruzzjonijiet tal-kumpanija li jistgħu jerġgħu jintużaw fiċ-chat',
    upload: 'Tella’ fajls',
    uploadHint: 'Żid materjal tas-sors mal-librerija tal-kumpanija',
    uploadLabel: 'Agħżel fajls biex ittella’',
    searching: 'Qed infittxu fl-ispazju tax-xogħol…',
    loading: 'Qed jitgħabba l-ispazju tax-xogħol…',
    limited:
      'It-tfittxija skont it-tifsira hija limitata temporanjament. Qed jintwerew it-tqabbiliet tat-test disponibbli.',
    failed: 'Bħalissa ma tistax tfittex.',
    retry: 'Erġa’ fittex',
    details: 'Dettalji',
    loadMore: 'Għabbi aktar',
    noResults: 'L-ebda riżultat għal “{query}”',
    ask: 'Staqsi lil Poneva dwar “{query}”',
    updated: 'Aġġornat {date}',
    navigate: 'Innaviga',
    open: 'Iftaħ',
    dismiss: 'Agħlaq',
    openFailed: 'Ma jistax jinfetaħ bħalissa. Erġa’ pprova.',
  },
  pl: {
    name: 'Szukaj',
    description:
      'Przeszukuj swoje czaty oraz pliki, Skills, wyniki, wiedzę i spotkania swojej firmy.',
    placeholder: 'Szukaj w obszarze roboczym',
    close: 'Zamknij wyszukiwanie',
    results: 'Wyniki wyszukiwania',
    groupActions: 'Działania',
    groupRecent: 'Ostatnie',
    groupChats: 'Czaty',
    groupResults: 'Wyniki',
    groupFiles: 'Pliki',
    groupSkills: 'Skills',
    groupKnowledge: 'Wiedza',
    groupMeetings: 'Spotkania',
    kindChat: 'Czat',
    kindFile: 'Plik',
    kindSkill: 'Skill',
    kindSkillDraft: 'Szkic Skilla',
    kindResult: 'Wynik',
    kindKnowledgePage: 'Zatwierdzona wiedza',
    kindMeeting: 'Spotkanie',
    newChat: 'Nowy czat',
    newChatHint: 'Rozpocznij nową rozmowę',
    createSkill: 'Utwórz Skill',
    createSkillHint: 'Twórz w czacie firmowe instrukcje wielokrotnego użytku',
    upload: 'Prześlij pliki',
    uploadHint: 'Dodaj materiały źródłowe do biblioteki firmy',
    uploadLabel: 'Wybierz pliki do przesłania',
    searching: 'Przeszukiwanie obszaru roboczego…',
    loading: 'Wczytywanie obszaru roboczego…',
    limited:
      'Wyszukiwanie według znaczenia jest tymczasowo ograniczone. Wyświetlane są dostępne dopasowania tekstu.',
    failed: 'Nie można teraz wyszukać.',
    retry: 'Szukaj ponownie',
    details: 'Szczegóły',
    loadMore: 'Wczytaj więcej',
    noResults: 'Brak wyników dla „{query}”',
    ask: 'Zapytaj Poneva o „{query}”',
    updated: 'Zaktualizowano {date}',
    navigate: 'Nawigacja',
    open: 'Otwórz',
    dismiss: 'Zamknij',
    openFailed: 'Nie można teraz tego otworzyć. Spróbuj ponownie.',
  },
  pt: {
    name: 'Pesquisar',
    description:
      'Pesquise nas suas conversas e nos ficheiros, Skills, resultados, conhecimento e reuniões da sua empresa.',
    placeholder: 'Pesquisar no espaço de trabalho',
    close: 'Fechar pesquisa',
    results: 'Resultados da pesquisa',
    groupActions: 'Ações',
    groupRecent: 'Recentes',
    groupChats: 'Conversas',
    groupResults: 'Resultados',
    groupFiles: 'Ficheiros',
    groupSkills: 'Skills',
    groupKnowledge: 'Conhecimento',
    groupMeetings: 'Reuniões',
    kindChat: 'Conversa',
    kindFile: 'Ficheiro',
    kindSkill: 'Skill',
    kindSkillDraft: 'Rascunho de Skill',
    kindResult: 'Resultado',
    kindKnowledgePage: 'Conhecimento aprovado',
    kindMeeting: 'Reunião',
    newChat: 'Nova conversa',
    newChatHint: 'Iniciar uma conversa nova',
    createSkill: 'Criar um Skill',
    createSkillHint: 'Criar instruções reutilizáveis da empresa na conversa',
    upload: 'Carregar ficheiros',
    uploadHint: 'Adicionar material de origem à biblioteca da empresa',
    uploadLabel: 'Escolher ficheiros para carregar',
    searching: 'A pesquisar no espaço de trabalho…',
    loading: 'A carregar o espaço de trabalho…',
    limited:
      'A pesquisa por significado está temporariamente limitada. A mostrar as correspondências de texto disponíveis.',
    failed: 'Não é possível pesquisar neste momento.',
    retry: 'Pesquisar novamente',
    details: 'Detalhes',
    loadMore: 'Carregar mais',
    noResults: 'Sem resultados para «{query}»',
    ask: 'Perguntar ao Poneva sobre «{query}»',
    updated: 'Atualizado a {date}',
    navigate: 'Navegar',
    open: 'Abrir',
    dismiss: 'Fechar',
    openFailed: 'Não foi possível abrir neste momento. Tente novamente.',
  },
  ro: {
    name: 'Căutare',
    description:
      'Căutați în chaturile dvs. și în fișierele, Skills, rezultatele, cunoștințele și ședințele companiei.',
    placeholder: 'Căutați în spațiul de lucru',
    close: 'Închideți căutarea',
    results: 'Rezultatele căutării',
    groupActions: 'Acțiuni',
    groupRecent: 'Recente',
    groupChats: 'Chaturi',
    groupResults: 'Rezultate',
    groupFiles: 'Fișiere',
    groupSkills: 'Skills',
    groupKnowledge: 'Cunoștințe',
    groupMeetings: 'Ședințe',
    kindChat: 'Chat',
    kindFile: 'Fișier',
    kindSkill: 'Skill',
    kindSkillDraft: 'Ciornă de Skill',
    kindResult: 'Rezultat',
    kindKnowledgePage: 'Cunoștințe aprobate',
    kindMeeting: 'Ședință',
    newChat: 'Chat nou',
    newChatHint: 'Începeți o conversație nouă',
    createSkill: 'Creați un Skill',
    createSkillHint: 'Creați în chat instrucțiuni reutilizabile ale companiei',
    upload: 'Încărcați fișiere',
    uploadHint: 'Adăugați materiale sursă în biblioteca companiei',
    uploadLabel: 'Alegeți fișierele de încărcat',
    searching: 'Se caută în spațiul de lucru…',
    loading: 'Se încarcă spațiul de lucru…',
    limited:
      'Căutarea după sens este limitată temporar. Se afișează potrivirile de text disponibile.',
    failed: 'Căutarea nu este disponibilă acum.',
    retry: 'Căutați din nou',
    details: 'Detalii',
    loadMore: 'Încărcați mai multe',
    noResults: 'Niciun rezultat pentru „{query}”',
    ask: 'Întrebați Poneva despre „{query}”',
    updated: 'Actualizat {date}',
    navigate: 'Navigare',
    open: 'Deschidere',
    dismiss: 'Închidere',
    openFailed: 'Nu se poate deschide acum. Încearcă din nou.',
  },
  sk: {
    name: 'Hľadať',
    description:
      'Prehľadávajte svoje chaty a súbory, Skills, výsledky, znalosti a stretnutia svojej firmy.',
    placeholder: 'Hľadať v pracovnom priestore',
    close: 'Zavrieť vyhľadávanie',
    results: 'Výsledky vyhľadávania',
    groupActions: 'Akcie',
    groupRecent: 'Nedávne',
    groupChats: 'Chaty',
    groupResults: 'Výsledky',
    groupFiles: 'Súbory',
    groupSkills: 'Skills',
    groupKnowledge: 'Znalosti',
    groupMeetings: 'Stretnutia',
    kindChat: 'Chat',
    kindFile: 'Súbor',
    kindSkill: 'Skill',
    kindSkillDraft: 'Koncept Skillu',
    kindResult: 'Výsledok',
    kindKnowledgePage: 'Schválené znalosti',
    kindMeeting: 'Stretnutie',
    newChat: 'Nový chat',
    newChatHint: 'Začať novú konverzáciu',
    createSkill: 'Vytvoriť Skill',
    createSkillHint: 'Vytvárajte v chate opakovane použiteľné firemné pokyny',
    upload: 'Nahrať súbory',
    uploadHint: 'Pridajte zdrojové materiály do firemnej knižnice',
    uploadLabel: 'Vyberte súbory na nahratie',
    searching: 'Prehľadáva sa pracovný priestor…',
    loading: 'Načítava sa pracovný priestor…',
    limited:
      'Vyhľadávanie podľa významu je dočasne obmedzené. Zobrazujú sa dostupné textové zhody.',
    failed: 'Vyhľadávanie teraz nie je k dispozícii.',
    retry: 'Hľadať znova',
    details: 'Podrobnosti',
    loadMore: 'Načítať ďalšie',
    noResults: 'Pre „{query}“ sa nenašli žiadne výsledky',
    ask: 'Opýtať sa Ponevy na „{query}“',
    updated: 'Aktualizované {date}',
    navigate: 'Prechádzať',
    open: 'Otvoriť',
    dismiss: 'Zavrieť',
    openFailed: 'Teraz sa to nedá otvoriť. Skúste to znova.',
  },
  sl: {
    name: 'Iskanje',
    description:
      'Iščite po svojih klepetih ter datotekah, Skills, rezultatih, znanju in sestankih svojega podjetja.',
    placeholder: 'Išči v delovnem prostoru',
    close: 'Zapri iskanje',
    results: 'Rezultati iskanja',
    groupActions: 'Dejanja',
    groupRecent: 'Nedavno',
    groupChats: 'Klepeti',
    groupResults: 'Rezultati',
    groupFiles: 'Datoteke',
    groupSkills: 'Skills',
    groupKnowledge: 'Znanje',
    groupMeetings: 'Sestanki',
    kindChat: 'Klepet',
    kindFile: 'Datoteka',
    kindSkill: 'Skill',
    kindSkillDraft: 'Osnutek Skilla',
    kindResult: 'Rezultat',
    kindKnowledgePage: 'Odobreno znanje',
    kindMeeting: 'Sestanek',
    newChat: 'Nov klepet',
    newChatHint: 'Začni nov pogovor',
    createSkill: 'Ustvari Skill',
    createSkillHint: 'V klepetu ustvarite navodila podjetja za večkratno uporabo',
    upload: 'Naloži datoteke',
    uploadHint: 'Dodajte izvorno gradivo v knjižnico podjetja',
    uploadLabel: 'Izberite datoteke za nalaganje',
    searching: 'Iskanje po delovnem prostoru…',
    loading: 'Nalaganje delovnega prostora…',
    limited: 'Iskanje po pomenu je začasno omejeno. Prikazani so razpoložljivi besedilni zadetki.',
    failed: 'Iskanje trenutno ni mogoče.',
    retry: 'Poskusi znova',
    details: 'Podrobnosti',
    loadMore: 'Naloži več',
    noResults: 'Ni rezultatov za »{query}«',
    ask: 'Vprašaj Poneva o »{query}«',
    updated: 'Posodobljeno {date}',
    navigate: 'Premikanje',
    open: 'Odpri',
    dismiss: 'Zapri',
    openFailed: 'Tega trenutno ni mogoče odpreti. Poskusite znova.',
  },
  es: {
    name: 'Buscar',
    description:
      'Busca en tus chats y en los archivos, Skills, resultados, conocimiento y reuniones de tu empresa.',
    placeholder: 'Buscar en tu espacio de trabajo',
    close: 'Cerrar búsqueda',
    results: 'Resultados de búsqueda',
    groupActions: 'Acciones',
    groupRecent: 'Recientes',
    groupChats: 'Chats',
    groupResults: 'Resultados',
    groupFiles: 'Archivos',
    groupSkills: 'Skills',
    groupKnowledge: 'Conocimiento',
    groupMeetings: 'Reuniones',
    kindChat: 'Chat',
    kindFile: 'Archivo',
    kindSkill: 'Skill',
    kindSkillDraft: 'Borrador de Skill',
    kindResult: 'Resultado',
    kindKnowledgePage: 'Conocimiento aprobado',
    kindMeeting: 'Reunión',
    newChat: 'Nuevo chat',
    newChatHint: 'Iniciar una conversación nueva',
    createSkill: 'Crear un Skill',
    createSkillHint: 'Crear instrucciones reutilizables de la empresa en el chat',
    upload: 'Subir archivos',
    uploadHint: 'Añadir material de origen a la biblioteca de la empresa',
    uploadLabel: 'Elegir archivos para subir',
    searching: 'Buscando en tu espacio de trabajo…',
    loading: 'Cargando tu espacio de trabajo…',
    limited:
      'La búsqueda por significado está limitada temporalmente. Se muestran las coincidencias de texto disponibles.',
    failed: 'Ahora mismo no se puede buscar.',
    retry: 'Volver a buscar',
    details: 'Detalles',
    loadMore: 'Cargar más',
    noResults: 'No hay resultados para «{query}»',
    ask: 'Preguntar a Poneva sobre «{query}»',
    updated: 'Actualizado el {date}',
    navigate: 'Navegar',
    open: 'Abrir',
    dismiss: 'Cerrar',
    openFailed: 'No se pudo abrir ahora mismo. Inténtalo de nuevo.',
  },
  sv: {
    name: 'Sök',
    description: 'Sök i dina chattar och i företagets filer, Skills, resultat, kunskap och möten.',
    placeholder: 'Sök i arbetsytan',
    close: 'Stäng sökningen',
    results: 'Sökresultat',
    groupActions: 'Åtgärder',
    groupRecent: 'Senaste',
    groupChats: 'Chattar',
    groupResults: 'Resultat',
    groupFiles: 'Filer',
    groupSkills: 'Skills',
    groupKnowledge: 'Kunskap',
    groupMeetings: 'Möten',
    kindChat: 'Chatt',
    kindFile: 'Fil',
    kindSkill: 'Skill',
    kindSkillDraft: 'Skill-utkast',
    kindResult: 'Resultat',
    kindKnowledgePage: 'Godkänd kunskap',
    kindMeeting: 'Möte',
    newChat: 'Ny chatt',
    newChatHint: 'Starta en ny konversation',
    createSkill: 'Skapa en Skill',
    createSkillHint: 'Bygg återanvändbara företagsinstruktioner i chatten',
    upload: 'Ladda upp filer',
    uploadHint: 'Lägg till källmaterial i företagets bibliotek',
    uploadLabel: 'Välj filer att ladda upp',
    searching: 'Söker i arbetsytan…',
    loading: 'Läser in arbetsytan…',
    limited: 'Betydelsesökningen är tillfälligt begränsad. Visar tillgängliga textträffar.',
    failed: 'Det går inte att söka just nu.',
    retry: 'Försök söka igen',
    details: 'Detaljer',
    loadMore: 'Visa fler',
    noResults: 'Inga resultat för ”{query}”',
    ask: 'Fråga Poneva om ”{query}”',
    updated: 'Uppdaterad {date}',
    navigate: 'Navigera',
    open: 'Öppna',
    dismiss: 'Stäng',
    openFailed: 'Det gick inte att öppna det just nu. Försök igen.',
  },
};
export default searchCopy;
